/**
 * External Imports
 */

import { connect, useDispatch } from 'react-redux';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

/**
 * Nexus Imports
 */
import { NeuIcon } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react';

/**
 * Internal Imports
 */
import TransitionalBarChart from '../../metricsChart/barChart/TransitionalBarChart';
import { RootState } from '../../../redux/store';
import { setIssueFilter } from '../../../redux/actions';
import { logReportsClick } from '../../../utils/analyticsHelpers';
/**
 * Style Imports
 */
import {
  HeaderContainer,
  TaskMetricContainer,
  TitleContainer
} from '../CustomMetricCharts.styles';
import { ApiMetrics, Filter } from '../../../config/interfaces';

interface MetricsData {
  totalRecordsCount: number;
  metrics: { label: string; value: number; color: string }[];
}

interface EmpTaskMetricChartProps {
  issueMetrics: ApiMetrics;
  employeeSummaryReportFilter: Filter;
}

const EmpTaskMetricChart: FC<EmpTaskMetricChartProps> = ({
  issueMetrics,
  employeeSummaryReportFilter
}) => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();

  const { '*': view } = useParams();
  const { logTrackingEvent } = useAnalyticsApi();

  const metricsData: MetricsData = useMemo(() => {
    return {
      totalRecordsCount: issueMetrics.totalRecordsCount,
      metrics: [
        {
          label: 'Opened',
          value: issueMetrics.openedRecordsCount,
          color: '#df5828'
        },
        {
          label: 'Completed',
          value: issueMetrics.completedRecordsCount,
          color: '#0077b5'
        }
      ]
    };
  }, [issueMetrics]);

  const openIssuesTable = () => {
    dispatch(
      setIssueFilter({
        ...employeeSummaryReportFilter,
        startDate:
          employeeSummaryReportFilter.startDate ??
          format(
            new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            'yyyy-MM-dd'
          ),
        category: '',
        subCategory: '',
        createdBy: '',
        employeeId: ''
      })
    );
    if (view)
      logTrackingEvent(logReportsClick(view?.replace('/', '-'), 'issue'));
    setTimeout(() => {
      navigateTo('/employees/issues', {
        state: { from: view }
      });
    }, 1000);
  };

  return (
    <TaskMetricContainer onClick={openIssuesTable}>
      <HeaderContainer style={{ paddingLeft: '4.5em' }}>
        <NeuIcon size="1x" color="blue" className="ms-1">
          flag
        </NeuIcon>
        <TitleContainer style={{ marginLeft: '0.5rem' }}>
          {issueMetrics.totalRecordsCount} Issues
        </TitleContainer>
      </HeaderContainer>
      <TransitionalBarChart metricsData={metricsData} />
    </TaskMetricContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    issueMetrics: state.TaskReducer.issueMetrics,
    employeeSummaryReportFilter: state.ReportReducer.employeeSummaryReportFilter
  };
};
export default connect(mapReduxStateToProps)(EmpTaskMetricChart);
